/**
 * Primary navigation model (APP-001 §6, §14).
 *
 * The single source of truth for sidebar entries, mobile navigation, and route
 * metadata. Routes the API remains authoritative for (plan gating) carry a
 * non-authoritative display hint rather than being hidden locally.
 */
import type { ComponentType, ReactNode } from 'react';
import {
  Activity,
  BookOpen,
  CreditCard,
  ExternalLink,
  FileStack,
  Gauge,
  HelpCircle,
  Key,
  Sparkles,
  User,
  Webhook,
} from '@/components/icons';
import { getAppLinks } from './links';

export interface NavItem {
  /** Route label in the UI. */
  label: string;
  /** Internal application path, or an external absolute URL. */
  to: string;
  /** Leading icon. */
  icon: ComponentType<{ className?: string }>;
  /** Non-authoritative status hint (APP-001 §14). */
  hint?: 'plan_required' | 'external';
  /** Optional short description for tooltips. */
  description?: string;
  external?: boolean;
}

export interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

export function getPrimaryNav(): NavSection[] {
  const links = getAppLinks();
  return [
    {
      id: 'main',
      title: 'Dashboard',
      items: [
        { label: 'Overview', to: '/dashboard', icon: Gauge, description: 'Account, plan, and usage summary' },
        {
          label: 'Playground',
          to: '/playground',
          icon: Sparkles,
          description: 'Test the API under your real plan and quota',
        },
        {
          label: 'API Keys',
          to: '/api-keys',
          icon: Key,
          description: 'Manage reveal-once API key secrets and scopes',
        },
        {
          label: 'Usage',
          to: '/usage',
          icon: Activity,
          description: 'Monthly quota window and request history',
        },
        {
          label: 'Bulk Scoring',
          to: '/bulk',
          icon: FileStack,
          description: 'Asynchronous CSV bulk scoring jobs',
        },
        {
          label: 'Webhooks',
          to: '/webhooks',
          icon: Webhook,
          description: 'Outbound webhook endpoints and delivery',
        },
        {
          label: 'Billing',
          to: '/billing',
          icon: CreditCard,
          description: 'Stripe subscription and entitlement state',
        },
        {
          label: 'Account',
          to: '/account',
          icon: User,
          description: 'Account name, preferences, session, and support',
        },
      ],
    },
    {
      id: 'resources',
      title: 'Resources',
      items: [
        {
          label: 'API Documentation',
          to: links.apiDocs,
          icon: BookOpen,
          external: true,
          hint: 'external',
          description: 'OpenAPI reference and guides',
        },
        {
          label: 'Support',
          to: links.support,
          icon: HelpCircle,
          external: true,
          hint: 'external',
          description: 'Contact support',
        },
      ],
    },
  ];
}

export function HINT_LABELS(): Record<NonNullable<NavItem['hint']>, ReactNode> {
  return {
    plan_required: 'Plan required',
    external: 'External',
  };
}

/** Icon used for external-link affordance. */
export function ExternalLinkIcon({ className }: { className?: string }) {
  return <ExternalLink className={className} />;
}
